import { AnimatePresence } from 'framer-motion'
import React, { lazy, Suspense, useMemo, useState, type ReactNode } from 'react'
import { ToastContext, type Toast } from './toastContext'

const ToastItem = lazy(() =>
  import('../../components/common/ToastItem').then((module) => ({ default: module.ToastItem }))
)

interface ToastProviderProps {
  children: ReactNode
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([])

  const showToast = (toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).slice(2, 9)
    const newToast: Toast = {
      ...toast,
      id,
      duration: toast.duration ?? 5000, // Default 5 seconds
    }

    setToasts((prev) => [...prev, newToast])

    // Auto-hide after duration
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        hideToast(id)
      }, newToast.duration)
    }
  }

  const hideToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }

  const contextValue = useMemo(() => ({ showToast, hideToast }), [])

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} onHide={hideToast} />
    </ToastContext.Provider>
  )
}

interface ToastContainerProps {
  toasts: Toast[]
  onHide: (id: string) => void
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onHide }) => {
  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[150] space-y-2 max-w-sm w-full">
      <AnimatePresence>
        {toasts.map((toast) => (
          <Suspense fallback={null} key={toast.id}>
            <ToastItem toast={toast} onHide={onHide} />
          </Suspense>
        ))}
      </AnimatePresence>
    </div>
  )
}
